import React, { useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import FormatTools from '../FormatTools/FormatTools';
import { toast } from 'react-hot-toast';

const AddConceptForm = ({ onAddConcept }) => {
  const [newConcept, setNewConcept] = useState('');
  const [isAddingConcept, setIsAddingConcept] = useState(false);
  const textareaRef = useRef(null);
  const [bulletPointMode, setBulletPointMode] = useState(false);
  const [numberedListMode, setNumberedListMode] = useState(false);
  const [listCounter, setListCounter] = useState(1);

  const handleAddConcept = async () => {
    if (newConcept.trim() !== '') {
      try {
        await onAddConcept(newConcept);
        setNewConcept('');
        setIsAddingConcept(false);
        setBulletPointMode(false);
        setNumberedListMode(false);
      } catch (error) {
        toast.error('Failed to add concept. Please try again.');
        console.error('Error adding concept:', error);
      }
    }
  };

  const formatSelection = (type, e) => {
    e?.preventDefault();
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    
    if (start === end) {
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      let formattedText;
      switch (type) {
        case 'bullet':
          formattedText = `${textBeforeCursor}• ${textAfterCursor}`;
          break;
        case 'number':
          formattedText = `${textBeforeCursor}1. ${textAfterCursor}`;
          break;
        case 'indent':
          formattedText = `${textBeforeCursor}    ${textAfterCursor}`;
          break;
        default:
          formattedText = text;
      }
      
      setNewConcept(formattedText);
      
      setTimeout(() => {
        const newPosition = cursorPosition + (type === 'bullet' ? 2 : type === 'number' ? 3 : 4);
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
      return;
    }

    const selectedText = text.substring(start, end);
    const lines = selectedText.split('\n');
    
    const formattedLines = lines.map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return '';
      
      switch (type) {
        case 'bullet':
          return line.startsWith('• ') ? line : `• ${trimmedLine}`;
        case 'number':
          return line.match(/^\d+\. /) ? line : `${index + 1}. ${trimmedLine}`;
        case 'indent':
          return line.startsWith('    ') ? line : `    ${trimmedLine}`;
        default:
          return line;
      }
    });

    const formattedText = formattedLines.join('\n');
    
    const newText = text.substring(0, start) + formattedText + text.substring(end);
    setNewConcept(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start,
        start + formattedText.length
      );
    }, 0);
  };

  const addBulletPoint = () => {
    setNumberedListMode(false);
    const newMode = !bulletPointMode;
    setBulletPointMode(newMode);
    if (newMode) {
      formatSelection('bullet');
    }
  };

  const addNumberedList = () => {
    setBulletPointMode(false);
    const newMode = !numberedListMode;
    setNumberedListMode(newMode);
    setListCounter(1);
    if (newMode) {
      formatSelection('number');
    }
  };

  const addIndentation = () => formatSelection('indent');
  const addParagraph = () => formatSelection('paragraph');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (bulletPointMode || numberedListMode)) {
      e.preventDefault();
      const textarea = textareaRef.current;
      const cursorPosition = textarea.selectionStart;
      const text = textarea.value;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const textAfterCursor = text.substring(cursorPosition);
      
      // Check if the current line is empty (just a bullet/number)
      const currentLine = textBeforeCursor.split('\n').pop();
      if (currentLine.trim() === '•' || currentLine.match(/^\d+\.$/)) {
        // If empty, remove the formatting and exit bullet/number mode
        const newText = textBeforeCursor.substring(0, textBeforeCursor.length - currentLine.length) + '\n' + textAfterCursor;
        setNewConcept(newText);
        setBulletPointMode(false);
        setNumberedListMode(false);
      } else {
        // Add new bullet point or number
        const newLine = bulletPointMode ? '\n• ' : `\n${listCounter + 1}. `;
        const newText = textBeforeCursor + newLine + textAfterCursor;
        setNewConcept(newText);
        if (numberedListMode) {
          setListCounter(prev => prev + 1);
        }
        
        // Set cursor position after the new bullet/number
        setTimeout(() => {
          const newPosition = cursorPosition + newLine.length;
          textarea.focus();
          textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    } else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleAddConcept();
    }
  };

  const handleFormatStateChange = (type) => {
    if (type === 'bullet') {
      setNumberedListMode(false);
      setBulletPointMode(prev => !prev);
    } else if (type === 'number') {
      setBulletPointMode(false);
      setNumberedListMode(prev => !prev);
      setListCounter(1);
    }
  };

  return (
    <>
      {isAddingConcept ? (
        <div className="flex flex-col space-y-2 mt-4 animate-fadeIn">
          <div className="relative">
            <textarea
              ref={textareaRef}
              placeholder="New concept..."
              value={newConcept}
              onChange={(e) => setNewConcept(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full p-2 pr-24 rounded-xl focus:outline-none border-2 border-blue-200 focus:border-blue-400 resize-y min-h-[100px] bg-neutral-50 transition-colors duration-200"
              autoFocus
            />
            <FormatTools
              onBulletPoint={addBulletPoint}
              onNumberedList={addNumberedList}
              onIndentation={addIndentation}
              onParagraph={addParagraph}
              bulletPointActive={bulletPointMode}
              numberedListActive={numberedListMode}
              conceptIndex="new"
              onFormatSelection={formatSelection}
              onFormatStateChange={handleFormatStateChange}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button 
              onClick={() => setIsAddingConcept(false)} 
              className="px-4 py-2 bg-white text-neutral-900 rounded-xl font-medium hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleAddConcept} 
              className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAddingConcept(true)}
          className="w-full flex items-center justify-center p-2 text-neutral-500 hover:text-neutral-700 mt-4 rounded-xl hover:bg-neutral-100 transition-colors"
        >
          <Plus size={16} className="mr-2" />
          Add a new concept...
        </button>
      )}
    </>
  );
};

export default AddConceptForm;
